"use client";

import { FlagIcon } from "@phosphor-icons/react/dist/ssr";
import { useTranslations } from "next-intl";

import EmptyStateMessage from "@/components/ui/emptyStateMessage";
import {
  homeProjectStatusTagColorMap,
  homeTagSolidColorClassMap,
} from "@/features/home/utils/homeTagColors";
import type { ProjectStatusOutput } from "@/types";
type StatusProjectCardProps = {
  data: ProjectStatusOutput | undefined;
};

export function StatusProjectCard({ data }: StatusProjectCardProps) {
  const t = useTranslations("home");
  const tGlossary = useTranslations("glossary");
  const isEmpty = !data || data.total === 0;

  return (
    <div className="flex w-full flex-1 flex-col gap-2">
      {isEmpty ? (
        <div className="flex h-35.75 items-center justify-center">
          <EmptyStateMessage
            message={t("empty.status")}
            icon={
              <span className="color-neutral-950">
                <FlagIcon />
              </span>
            }
            compact
          />
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center gap-2">
            <div className="flex size-6 shrink-0 flex-row items-center justify-center gap-[5.54px] rounded-lg bg-[#D5F2F0] p-0">
              <span className="text-[#1A1A1A] [&_svg]:size-[14px]">
                <FlagIcon />
              </span>
            </div>
            <span className="heading-6 translate-y-0.5 text-neutral-950">
              {t("section.status")}
            </span>
          </div>

          {/* Card */}
          <div className="flex flex-col rounded-md bg-neutral-50">
            <div className="flex w-full px-4 pt-3 pb-2 xl:pt-4 xl:pb-3">
              {data!.items.map((item) => (
                <div
                  key={item.status}
                  className={`h-2 rounded-3xl ${homeTagSolidColorClassMap[homeProjectStatusTagColorMap[item.status]]}`}
                  style={{ flex: item.percentage }}
                />
              ))}
            </div>

            {[data!.items.slice(0, 3), data!.items.slice(3)].map(
              (row, rowIndex) =>
                row.length > 0 && (
                  <div
                    key={rowIndex}
                    className="flex items-start gap-2 border-t border-neutral-100 px-4 py-3"
                  >
                    {row.map((item) => (
                      <div key={item.status} className="flex flex-1 items-start gap-1">
                        <div className="flex size-4 shrink-0 items-center justify-center">
                          <div
                            className={`size-1.75 rounded-full ${homeTagSolidColorClassMap[homeProjectStatusTagColorMap[item.status]]}`}
                          />
                        </div>
                        <div className="flex min-w-0 flex-col gap-1">
                          <span className="body-xs text-neutral-950">
                            {tGlossary(`projectStatus.${item.status}`)}
                          </span>
                          <span className="body-xs text-neutral-600">
                            {item.count} ({Math.round(item.percentage)}%)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )
            )}
          </div>
        </>
      )}
    </div>
  );
}
